// Contexto Dual para la UI (espejo de backend/app/services/dual_yoyo_context.py).
// Una alarma Dual compara la presión (PSI) entre dos posiciones duales
// (hermanas). NO debe mostrarse como una alarma de presión individual: hay que
// dejar claro que es Dual, con ambas posiciones, la diferencia y el umbral.

import i18n from '../i18n/config';

const toInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
};

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const fmt = (v) => {
    if (v == null) return '—';
    const r = Math.round(Number(v) * 10) / 10;
    return Number.isInteger(r) ? `${r}` : r.toFixed(1);
};

export const isDualYoyoTicket = (ticket) => {
    if (!ticket) return false;
    if (`${ticket.alarm_reason || ''}`.toLowerCase() === 'dual_yoyo') return true;
    if (`${ticket.breach_type || ''}`.toUpperCase() === 'DUAL_YOYO') return true;
    if (`${ticket.rule_code || ''}`.toLowerCase().startsWith('dual_yoyo')) return true;
    const meta = ticket.evaluation_metadata || ticket.metadata || {};
    if (`${meta.evaluation_type || ''}`.toLowerCase() === 'dual_yoyo') return true;
    if (meta.dual_yoyo === true) return true;
    return false;
};

export const buildDualContext = (ticket) => {
    if (!ticket) return null;
    const meta = ticket.evaluation_metadata || ticket.metadata || {};
    if (!meta || Object.keys(meta).length === 0) {
        // Sin metadata pero es dual: devolver contexto mínimo.
        if (!isDualYoyoTicket(ticket)) return null;
    }

    const positionsSet = [];
    (meta.group_positions || []).forEach((p) => {
        const i = toInt(p);
        if (i != null && !positionsSet.includes(i)) positionsSet.push(i);
    });
    const primaryPosition = toInt(meta.primary_position);
    const peerReadingsRaw = meta.peer_readings || {};
    const peerReadings = {};
    Object.keys(peerReadingsRaw).forEach((k) => {
        const pos = toInt(k);
        const val = toNum(peerReadingsRaw[k]);
        if (pos != null && val != null) peerReadings[pos] = val;
        if (pos != null && !positionsSet.includes(pos)) positionsSet.push(pos);
    });
    if (primaryPosition != null && !positionsSet.includes(primaryPosition)) positionsSet.push(primaryPosition);
    positionsSet.sort((a, b) => a - b);

    const primaryValue = toNum(meta.primary_value) ?? toNum(meta.current_value);
    const referenceValue = toNum(meta.reference_value);
    let differencePsi = toNum(meta.difference_psi);
    if (differencePsi == null && primaryValue != null && referenceValue != null) {
        differencePsi = Math.round(Math.abs(primaryValue - referenceValue) * 10) / 10;
    }
    const differencePercent = toNum(meta.difference_percent);
    const thresholdPercent = toNum(meta.allowed_difference_percent);
    const thresholdPsi = toNum(meta.threshold_psi_approx);

    let secondaryPosition = null;
    let secondaryValue = null;
    Object.keys(peerReadings).forEach((k) => {
        const pos = toInt(k);
        if (pos != null && pos !== primaryPosition && secondaryPosition == null) {
            secondaryPosition = pos;
            secondaryValue = peerReadings[pos];
        }
    });

    const pairText = positionsSet.length >= 2
        ? positionsSet.join(' y ')
        : (positionsSet[0] != null ? `${positionsSet[0]}` : '—');
    const positionsLabel = positionsSet.length ? i18n.t('hardcoded.utils_dualYoyo.dual_position', { pairText }) : 'Dual';

    let valueActualDisplay = '—';
    if (differencePsi != null && primaryValue != null && secondaryValue != null) {
        valueActualDisplay = `${fmt(differencePsi)} PSI (${fmt(primaryValue)} - ${fmt(secondaryValue)})`;
    } else if (differencePsi != null) {
        valueActualDisplay = `${fmt(differencePsi)} PSI`;
    }

    let rangeDisplay = i18n.t('hardcoded.utils_dualYoyo.diff_per_profile');
    if (thresholdPercent != null && thresholdPsi != null) {
        rangeDisplay = `Dif. ${fmt(thresholdPercent)}% (≈${fmt(thresholdPsi)} PSI)`;
    } else if (thresholdPercent != null) {
        rangeDisplay = `Dif. ${fmt(thresholdPercent)}%`;
    }

    const stalePositions = (meta.stale_positions || []).map(toInt).filter((x) => x != null);

    return {
        isDualYoyo: true,
        suspended: meta.suspended === true,
        stalePositions,
        positions: positionsSet,
        pairText,
        positionsLabel,
        primaryPosition,
        primaryValue,
        secondaryPosition,
        secondaryValue,
        referenceValue,
        differencePsi,
        differencePercent,
        thresholdPercent,
        thresholdPsi,
        axleNumber: toInt(meta.axle_number),
        side: meta.side,
        profileName: meta.profile_name,
        valueActualDisplay,
        rangeDisplay,
        primaryDisplay: (primaryPosition != null && primaryValue != null)
            ? `Pos. ${primaryPosition} · ${fmt(primaryValue)} PSI` : null,
        secondaryDisplay: (secondaryPosition != null && secondaryValue != null)
            ? `Pos. ${secondaryPosition} · ${fmt(secondaryValue)} PSI` : null,
    };
};
